import type { PluginContext, RequestMetrics } from '../core/types';

type State = 'playing' | 'buffering' | 'starting' | 'paused' | 'offline' | 'seeking' | 'unobserved';
type Recovery = { id: string; at: number; action: string; reason: string; attempt?: number; engineAtStart: string };
type RequestSummary = {
  requestKind: string; serverName?: string; server?: string; statusCode?: number;
  count: number; failedCount: number; timeoutCount: number; bytes: number; retryCount: number;
  ttfbCount: number; ttfbTotalMs: number; ttfbMaxMs: number;
  downloadCount: number; downloadTotalMs: number; downloadMaxMs: number;
  ttfbBuckets: number[]; downloadBuckets: number[];
};
const PERIOD_MS = 60_000;
const MAX_OBSERVATION_GAP_MS = 5000;
const TIMING_BUCKET_BOUNDS_MS = [50, 100, 250, 500, 1000, 2500, 5000, 10000, 30000];

/** QoE intervals are deltas. Sequence numbers on the envelope make them deduplicable. */
export class QoeTelemetry {
  private timer: ReturnType<typeof setInterval>;
  private destroyed = false;
  private unsubs: Array<() => void> = [];
  private lastAt = Date.now();
  private intervalStartedAt = this.lastAt;
  private lastTime: number;
  private progressTime: number;
  private progressAt = this.lastAt;
  private hasPlayed = false;
  private offline = false;
  private startup: { id: number; at: number } | null = null;
  private startupSequence = 0;
  private startupFinished = false;
  private recovery: Recovery | null = null;
  private recoverySequence = 0;
  private durations = this.emptyDurations();
  private visiblePlayedMs = 0;
  private visibleBufferingMs = 0;
  private lastPlaybackProgressAt = 0;
  private exposureContext: Record<string, unknown>;
  private frames = { totalFrames: 0, droppedFrames: 0 };
  private frameBaseline: { video: HTMLVideoElement; total: number; dropped: number } | null = null;
  private frameSamples = 0;
  private summaries = new Map<string, RequestSummary>();
  private requestIntervalStartedAt = this.lastAt;
  private lastRequestError = new Map<string, number>();

  constructor(private ctx: PluginContext,
    private emit: (name: string, data: Record<string, unknown>) => void,
    private activeLag: () => { lagId: string; durationMs: number } | undefined,
    private onObservation?: (time: number, elapsedMs: number, mode: State, at: number) => void) {
    this.lastTime = this.progressTime = ctx.store.getState().currentTime;
    this.exposureContext = this.readExposureContext();
    const bus = ctx.eventBus;
    this.unsubs.push(
      bus.on('playback:requested', () => this.requestPlayback()),
      bus.on('play', () => this.requestPlayback()),
      bus.on('playback:blocked', () => this.finishStartup('blocked')),
      bus.on('playback:cancelled', () => { this.finishStartup('cancelled'); this.finishRecovery('cancelled'); }),
      bus.on('playing', () => {
        // Rebase after restart/seek: a timeline jump is not successful recovery.
        this.progressTime = this.ctx.getEngine()?.getVideoElement?.()?.currentTime ?? this.ctx.store.getState().currentTime;
        this.progressAt = Date.now();
      }),
      bus.on('video:seeking', () => {
        this.progressTime = this.ctx.getEngine()?.getVideoElement?.()?.currentTime ?? this.ctx.store.getState().currentTime;
        this.progressAt = Date.now();
      }),
      bus.on('stream:offline', () => this.markOffline()),
      bus.on('stream:online', () => { this.offline = false; }),
      bus.on('hls:playlist:loaded', () => { this.offline = false; }),
      bus.on('error', e => { if (e.fatal) this.finishStartup(this.offline || !ctx.store.getState().isOnline ? 'offline' : 'error', e.code); }),
      bus.on('hls:error', e => {
        if (e.responseCode === 404 && /^(manifest|level|audioTrack|subtitleTrack)LoadError$/.test(e.details)) this.markOffline(404);
      }),
      bus.on('hls:request', data => this.request(data)),
      bus.on('playback:recovery', data => this.beginRecovery(data)),
      bus.on('latency:fallback', () => this.beginRecovery({ action: 'fallback', reason: 'latency_unstable' })),
      bus.on('stopped', () => {
        // The old engine stops during a seamless fallback. Its cleanup is not
        // a user stop and must not prematurely complete/cancel that recovery.
        if (ctx.store.getState().isSwitching) return;
        this.tick(); this.finishStartup('cancelled'); this.finishRecovery('cancelled'); this.flush();
      }),
      ctx.store.subscribe(s => s.isPlaying, playing => {
        if (!playing && this.hasPlayed && !ctx.store.getState().isSwitching) this.finishRecovery('cancelled');
      }),
      ctx.store.subscribe(s => JSON.stringify([s.engineName, s.cdnServer, s.cdnServerIp,
        s.cdnServerSource, s.quality, s.streamKey]), () => this.syncExposureContext()),
    );
    const visibilityChanged = () => this.syncExposureContext();
    document.addEventListener('visibilitychange', visibilityChanged);
    this.unsubs.push(() => document.removeEventListener('visibilitychange', visibilityChanged));
    const flushOnPageHide = () => { this.tick(); this.flush(); };
    window.addEventListener('pagehide', flushOnPageHide);
    this.unsubs.push(() => window.removeEventListener('pagehide', flushOnPageHide));
    if (ctx.config.autoplay) this.requestPlayback();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  private emptyDurations(): Record<State, number> {
    return { playing: 0, buffering: 0, starting: 0, paused: 0, offline: 0, seeking: 0, unobserved: 0 };
  }

  private readExposureContext(): Record<string, unknown> {
    const state = this.ctx.store.getState();
    const video = this.ctx.getEngine()?.getVideoElement?.();
    const channelId = Number(this.ctx.config.apiData?.channel_id ?? state.streamKey);
    return { engine: state.engineName, quality: state.quality,
      ch: Number.isFinite(channelId) && channelId > 0 ? channelId : undefined,
      serverName: state.cdnServer ?? undefined, server: state.cdnServerIp ?? undefined,
      serverSource: state.cdnServerSource ?? undefined,
      visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
      videoWidth: video?.videoWidth || undefined, videoHeight: video?.videoHeight || undefined };
  }

  private syncExposureContext(): void {
    const next = this.readExposureContext();
    if (JSON.stringify(next) === JSON.stringify(this.exposureContext)) return;
    const now = Date.now();
    // We cannot attribute the unsampled fraction across a context transition.
    this.durations.unobserved += Math.max(0, now - this.lastAt);
    this.lastAt = now;
    this.flush(); // Emit the saved context, before adopting the new one.
    this.exposureContext = next;
    this.lastTime = this.progressTime = this.ctx.getEngine()?.getVideoElement?.()?.currentTime
      ?? this.ctx.store.getState().currentTime;
    this.progressAt = now;
    this.frameBaseline = null;
  }

  private requestPlayback(): void {
    if (this.startupFinished || this.startup) return;
    this.startup = { id: ++this.startupSequence, at: Date.now() };
    this.emit('hls_startup_attempt', { startupId: this.startup.id });
    if (this.offline || !this.ctx.store.getState().isOnline) this.finishStartup('offline');
  }

  private finishStartup(result: string, statusCode?: number): void {
    if (!this.startup) return;
    this.emit('hls_startup_result', { startupId: this.startup.id, startupMs: Date.now() - this.startup.at, result, statusCode });
    this.startup = null;
    if (result === 'playing') this.startupFinished = true;
  }

  private markOffline(statusCode?: number): void {
    this.offline = true;
    this.finishStartup('offline', statusCode);
    this.finishRecovery('offline');
  }

  observeProgress(currentTime: number): void {
    const now = Date.now();
    const delta = currentTime - this.progressTime;
    const elapsed = Math.max(0, now - this.progressAt);
    this.progressTime = currentTime;
    this.progressAt = now;
    const state = this.ctx.store.getState();
    const video = this.ctx.getEngine()?.getVideoElement?.();
    const rate = Math.max(1, video?.playbackRate ?? 1);
    if (elapsed > MAX_OBSERVATION_GAP_MS || !Number.isFinite(delta) || delta <= 0.02 || delta > elapsed / 1000 * rate + 0.5 ||
        !state.isPlaying || !state.isOnline || this.offline || state.loading || video?.seeking || video?.paused) return;
    this.hasPlayed = true;
    this.lastPlaybackProgressAt = now;
    this.finishStartup('playing');
    if (!this.recovery || this.recovery.action !== 'fallback' ||
        (!state.isSwitching && state.engineName !== this.recovery.engineAtStart)) this.finishRecovery('recovered');
  }

  private beginRecovery(data: { action: string; reason: string; attempt?: number }): void {
    if (this.offline || !this.ctx.store.getState().isOnline) return;
    this.finishRecovery('superseded');
    this.recovery = { ...data, at: Date.now(), id: `recovery_${++this.recoverySequence}`, engineAtStart: this.ctx.store.getState().engineName };
    this.emit('hls_recovery', { recoveryId: this.recovery.id, ...data, phase: 'attempt', activeLagId: this.activeLag()?.lagId });
  }

  private finishRecovery(result: string): void {
    if (!this.recovery) return;
    const recovery = this.recovery;
    this.recovery = null;
    this.emit('hls_recovery', { recoveryId: recovery.id, action: recovery.action, reason: recovery.reason,
      attempt: recovery.attempt, phase: 'result', result, elapsedMs: Date.now() - recovery.at,
      activeLagId: this.activeLag()?.lagId });
  }

  private tick(): void {
    this.syncExposureContext();
    const now = Date.now();
    const elapsed = now - this.lastAt;
    this.lastAt = now;
    const state = this.ctx.store.getState();
    const video = this.ctx.getEngine()?.getVideoElement?.();
    const time = video?.currentTime ?? state.currentTime;
    const delta = time - this.lastTime;
    this.lastTime = time;
    this.observeProgress(time);
    const progresses = delta > 0.02 && delta <= elapsed / 1000 * Math.max(1, video?.playbackRate ?? 1) + 0.5;
    const mode: State = elapsed > MAX_OBSERVATION_GAP_MS || state.isSwitching ? 'unobserved'
      : this.offline || !state.isOnline ? 'offline'
      : video?.seeking ? 'seeking'
      : !state.isPlaying || video?.paused ? this.startup ? 'starting' : 'paused'
      : progresses && !state.loading && !video?.paused ? 'playing'
      : this.hasPlayed ? 'buffering' : 'starting';
    this.durations[mode] += Math.max(0, elapsed);
    if (mode === 'playing' && document.visibilityState !== 'hidden') this.visiblePlayedMs += elapsed;
    if (mode === 'buffering' && document.visibilityState !== 'hidden') this.visibleBufferingMs += elapsed;
    this.onObservation?.(time, Math.max(0, elapsed), mode, now);
    this.collectFrames(video, mode === 'playing');
    if (this.startup && now - this.startup.at >= 30_000) this.finishStartup('timeout');
    if (this.recovery && now - this.recovery.at >= 30_000) this.finishRecovery('no_progress');
    if (now - this.intervalStartedAt >= PERIOD_MS) this.flush(mode);
  }

  private collectFrames(video: HTMLVideoElement | null | undefined, count: boolean): void {
    if (!video?.getVideoPlaybackQuality) { this.frameBaseline = null; return; }
    try {
      const quality = video.getVideoPlaybackQuality();
      const total = quality.totalVideoFrames, dropped = quality.droppedVideoFrames;
      if (!Number.isFinite(total) || !Number.isFinite(dropped)) { this.frameBaseline = null; return; }
      const previous = this.frameBaseline;
      if (count && previous?.video === video && total >= previous.total && dropped >= previous.dropped) {
        this.frames.totalFrames += total - previous.total;
        this.frames.droppedFrames += dropped - previous.dropped;
        this.frameSamples++;
      }
      this.frameBaseline = { video, total, dropped };
    } catch { this.frameBaseline = null; }
  }

  private request(data: RequestMetrics): void {
    if (data.result === 'success' && (data.retryCount ?? 0) > 0) this.emit('hls_recovery', {
      action: 'retry', reason: 'loader_retry', phase: 'result', result: 'request_completed',
      requestKind: data.requestKind, retryCount: data.retryCount, activeLagId: this.activeLag()?.lagId,
    });
    const key = JSON.stringify([data.requestKind, data.serverName, data.server, data.statusCode]);
    let summary = this.summaries.get(key);
    if (!summary) {
      // Flush at the bound instead of accumulating unbounded server/IP labels.
      if (this.summaries.size >= 64) this.flushRequests();
      summary = { requestKind: data.requestKind, serverName: data.serverName, server: data.server, statusCode: data.statusCode,
        count: 0, failedCount: 0, timeoutCount: 0, bytes: 0, retryCount: 0,
        ttfbCount: 0, ttfbTotalMs: 0, ttfbMaxMs: 0, downloadCount: 0, downloadTotalMs: 0, downloadMaxMs: 0,
        ttfbBuckets: new Array(TIMING_BUCKET_BOUNDS_MS.length + 1).fill(0),
        downloadBuckets: new Array(TIMING_BUCKET_BOUNDS_MS.length + 1).fill(0) };
      this.summaries.set(key, summary);
    }
    summary.count++;
    summary.failedCount += data.result === 'success' ? 0 : 1;
    summary.timeoutCount += data.result === 'timeout' ? 1 : 0;
    summary.bytes += data.bytes ?? 0;
    summary.retryCount += data.retryCount ?? 0;
    if (data.ttfbMs !== undefined) {
      summary.ttfbCount++; summary.ttfbTotalMs += data.ttfbMs; summary.ttfbMaxMs = Math.max(summary.ttfbMaxMs, data.ttfbMs);
      this.timingBucket(summary.ttfbBuckets, data.ttfbMs);
    }
    if (data.downloadMs !== undefined) {
      summary.downloadCount++; summary.downloadTotalMs += data.downloadMs; summary.downloadMaxMs = Math.max(summary.downloadMaxMs, data.downloadMs);
      this.timingBucket(summary.downloadBuckets, data.downloadMs);
    }
    if (data.result !== 'success') {
      const errorKey = `${data.requestKind}:${data.statusCode ?? 0}:${data.result}`;
      const now = Date.now(), previous = this.lastRequestError.get(errorKey);
      if (previous === undefined || now - previous >= 30_000) {
        if (this.lastRequestError.size >= 64) this.lastRequestError.clear();
        this.lastRequestError.set(errorKey, now);
        this.emit('hls_request_error', { ...data, diagnosticOnly: true });
      }
    }
  }

  private flushRequests(): void {
    for (const summary of this.summaries.values()) this.emit('hls_request_summary', { ...summary,
      intervalStartedAt: this.requestIntervalStartedAt,
      intervalMs: Date.now() - this.requestIntervalStartedAt,
      timingBucketBoundsMs: TIMING_BUCKET_BOUNDS_MS,
      ttfbAvgMs: summary.ttfbCount ? summary.ttfbTotalMs / summary.ttfbCount : undefined,
      downloadAvgMs: summary.downloadCount ? summary.downloadTotalMs / summary.downloadCount : undefined,
    });
    this.summaries.clear();
    this.requestIntervalStartedAt = Date.now();
  }

  private timingBucket(buckets: number[], value: number): void {
    const index = TIMING_BUCKET_BOUNDS_MS.findIndex(bound => value <= bound);
    buckets[index === -1 ? TIMING_BUCKET_BOUNDS_MS.length : index]++;
  }

  private flush(state?: State): void {
    const elapsed = Date.now() - this.intervalStartedAt;
    if (elapsed > 0) {
      this.emit('hls_playback_exposure', { intervalStartedAt: this.intervalStartedAt, intervalMs: elapsed,
        ...this.exposureContext,
        playedMs: this.durations.playing, bufferingMs: this.durations.buffering, startupMs: this.durations.starting,
        pausedMs: this.durations.paused, offlineMs: this.durations.offline, seekingMs: this.durations.seeking,
        unobservedMs: this.durations.unobserved, visiblePlayedMs: this.visiblePlayedMs,
        visibleBufferingMs: this.visibleBufferingMs, lastPlaybackProgressAt: this.lastPlaybackProgressAt || undefined, state,
        activeLagId: this.activeLag()?.lagId,
      });
      if (this.frameSamples) this.emit('hls_video_frames', { ...this.exposureContext, ...this.frames, intervalMs: elapsed,
        droppedFrameRatio: this.frames.totalFrames ? this.frames.droppedFrames / this.frames.totalFrames : 0 });
    }
    this.intervalStartedAt = Date.now();
    this.durations = this.emptyDurations(); this.visiblePlayedMs = 0; this.visibleBufferingMs = 0;
    this.frames = { totalFrames: 0, droppedFrames: 0 }; this.frameSamples = 0;
    this.flushRequests();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    clearInterval(this.timer);
    this.tick();
    this.unsubs.forEach(fn => fn()); this.unsubs = [];
    this.finishStartup('cancelled'); this.finishRecovery('cancelled'); this.flush();
  }
}
